import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";

import Button, { ButtonProps } from "../../button/Button";
import useThemeService from "../../../services/theme-service/ThemeService";
import { RunAI } from "../../svg/svgImageList";
import { translationService } from "../../../services/translation/TranslationService";
import useModal from "../../dialog/useModal";
import InformationBeforeAction from "../../data-components/InformationBeforeAction";
import WarningBeforeAction from "../../data-components/WarningBeforeAction";
import Checkbox from "../../checkbox/Checkbox";
import { api } from "../../../services/api/ApiService";
import { SubscriptionPlanUsageStorageModel } from "../../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import { OrganizationSubscriptionPlansStorageModel } from "../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { getLocalPrice } from "../../../helpers/MathHelpers";
import { RunVideoAnalysisServiceModel } from "../../../services/inspection/RunVideoAnalysisService";
import "./RunVideoAnalysisButton.scss";

export interface RunVideoAnalysisButtonProps extends ButtonProps {
  disabled?: boolean;
  confirmationAction: () => void;
}

export default observer(function RunVideoAnalysisButton(props: RunVideoAnalysisButtonProps) {
  const { themeClass } = useThemeService();
  const { toggleModal: toggleConfirmationModal, isVisible: confirmationModalVisible } = useModal();
  const { toggleModal: toggleExcedeCallsModal, isVisible: excedeCallsModalVisible } = useModal();
  const { disabled, confirmationAction } = props;

  const [subscriptionPlanUsageStorage] = useState(() =>
    SubscriptionPlanUsageStorageModel.create({ autoLoad: false }, { api })
  );

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  const [runVideoAnalysisService] = useState(() => RunVideoAnalysisServiceModel.create({}, {}));

  const onClick = useCallback(async () => {
    const videoCallsUsage =
      await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();

    if (runVideoAnalysisService.VACallsStillAvailableAndPopupWasNotDismissed(videoCallsUsage)) {
      toggleConfirmationModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasPreviouslyDismissed(videoCallsUsage)
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      toggleConfirmationModal();
      return;
    }

    if (runVideoAnalysisService.VACallsNotAvailableAndPopupWasNotDismissed(videoCallsUsage)) {
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleExcedeCallsModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInThisBillingCycle(
        videoCallsUsage
      )
    ) {
      toggleConfirmationModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInPreviousBillingCycle(
        videoCallsUsage
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleExcedeCallsModal();
      return;
    }
  }, [
    organizationSubscriptionPlansStorage,
    runVideoAnalysisService,
    subscriptionPlanUsageStorage,
    toggleConfirmationModal,
    toggleExcedeCallsModal,
  ]);

  const handleConfirmationAction = () => {
    if (runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected) {
      runVideoAnalysisService.disableVACallsExceededWarningMessageForOrganization();
    }
    confirmationAction();
    toggleExcedeCallsModal();
  };

  useEffect(() => {
    runVideoAnalysisService.setIsDoNotShowVACallsExceededWarningMessageAgainSelected(false);
  }, [excedeCallsModalVisible, runVideoAnalysisService]);

  return (
    <>
      <Button
        id="run-ai"
        onClick={onClick}
        disabled={disabled}
        icon={{
          svg: RunAI,
          textKey: "RunVideoAnalysisButton.run",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-video"]),
        }}
      />
      <WarningBeforeAction
        isVisible={confirmationModalVisible}
        toggleModal={toggleConfirmationModal}
        titleKey="damages.RunVideoAnalysis.confirmation-warning-title"
        buttonOk={{
          id: "run-va-confirmation",
          textKey: "RunVideoAnalysisButton.run",
          action: () => {
            confirmationAction();
            toggleConfirmationModal();
          },
        }}
      >
        {translationService.translate("damages.RunVideoAnalysis.confirmation-warning-subtitle")}
      </WarningBeforeAction>
      <InformationBeforeAction
        isVisible={excedeCallsModalVisible}
        toggleModal={toggleExcedeCallsModal}
        titleKey="damages.RunVideoAnalysis.information-warning-title"
        buttonOk={{
          id: "run-va-ok",
          textKey: "general.ok",
          action: handleConfirmationAction,
        }}
      >
        <>
          {translationService.translate(
            "damages.RunVideoAnalysis.information-warning-subtitle-part-1"
          )}
          <Link to={"/subscription/plans"}>
            {translationService.translate(
              "damages.RunVideoAnalysis.information-warning-subtitle-part-2"
            )}
          </Link>
          {translationService.translate(
            "damages.RunVideoAnalysis.information-warning-subtitle-part-3",
            {
              price: getLocalPrice(
                organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice
              ),
            }
          )}
        </>
        <Checkbox
          className="run-ai-warning-message-checkbox"
          checked={runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected}
          onChange={runVideoAnalysisService.toggleIsDoNotShowThisPopupAgainSelected}
          labelKey="general.dont-show-this-message-again"
        />
      </InformationBeforeAction>
    </>
  );
});
